<template>
  <q-page padding>
    <q-card>
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">Notificações</div>
        <q-space />
        <q-toggle
          v-if="pushSuportado && pushConfigurado"
          :model-value="pushAtivo"
          :disable="pushProcessando"
          color="primary"
          icon="notifications_active"
          label="Avisos no dispositivo"
          id="toggle-push-notificacoes"
          class="q-mr-md"
          @update:model-value="alternarPush"
        >
          <q-tooltip>
            Recebe um aviso do navegador mesmo com o sistema fechado
          </q-tooltip>
        </q-toggle>
        <q-btn
          flat
          dense
          no-caps
          color="primary"
          icon="done_all"
          label="Marcar todas como lidas"
          id="btn-marcar-todas-lidas"
          :disable="notificacoesLoading || notificacoes.length === 0"
          @click="marcarTodas()"
        />
      </q-card-section>

      <q-card-section class="q-pt-none">
        <q-tabs
          v-model="filtro"
          dense
          align="left"
          class="text-grey"
          active-color="primary"
          indicator-color="primary"
          narrow-indicator
          @update:model-value="alterarFiltro"
        >
          <q-tab name="todas" label="Tudo" id="tab-notificacoes-todas" />
          <q-tab name="nao_lidas" label="Não lidas" id="tab-notificacoes-nao-lidas" />
          <q-tab name="lidas" label="Lidas" id="tab-notificacoes-lidas" />
        </q-tabs>
      </q-card-section>

      <q-separator />

      <q-card-section v-if="notificacoesLoading" class="text-center q-pa-lg">
        <q-spinner color="primary" size="2em" />
      </q-card-section>

      <q-card-section
        v-else-if="notificacoes.length === 0"
        class="text-center text-grey q-pa-lg"
      >
        Nenhuma notificação por aqui.
      </q-card-section>

      <q-list v-else separator>
        <q-item
          v-for="notificacao in notificacoes"
          :key="notificacao.id"
          clickable
          v-ripple
          :class="{ 'bg-blue-1': !notificacao.lido }"
          @click="abrir(notificacao)"
        >
          <q-item-section avatar top>
            <q-avatar
              :color="corDaSeveridade(notificacao.severidade)"
              text-color="white"
              :icon="notificacao.icone || 'notifications'"
              size="38px"
            />
          </q-item-section>

          <q-item-section>
            <q-item-label class="row items-center no-wrap">
              <span :class="{ 'text-weight-bold': !notificacao.lido }">
                {{ notificacao.titulo }}
              </span>
              <q-badge
                v-if="notificacao.contador > 1"
                color="grey-7"
                class="q-ml-sm"
                :label="notificacao.contador + 'x'"
              />
            </q-item-label>
            <q-item-label caption lines="2">
              {{ notificacao.mensagem }}
              <q-icon
                v-if="notificacao.tem_conteudo"
                name="article"
                size="16px"
                class="q-ml-xs text-primary"
              >
                <q-tooltip>Clique para ler o conteúdo</q-tooltip>
              </q-icon>
            </q-item-label>
            <q-item-label caption class="text-grey-6">
              {{ $fromNow(ultimaOcorrencia(notificacao)) }}
              <span v-if="notificacao.contador > 1">
                · primeira {{ $fromNow(notificacao.created) }}
              </span>
            </q-item-label>
          </q-item-section>

          <q-item-section side>
            <div class="row items-center no-wrap">
              <q-btn
                flat
                round
                dense
                size="sm"
                :icon="notificacao.lido ? 'mark_email_unread' : 'drafts'"
                @click.stop="alternarLeitura(notificacao)"
              >
                <q-tooltip>
                  {{ notificacao.lido ? "Marcar como não lido" : "Marcar como lido" }}
                </q-tooltip>
              </q-btn>
              <q-btn
                flat
                round
                dense
                size="sm"
                icon="delete"
                @click.stop="excluir(notificacao)"
              >
                <q-tooltip>Excluir</q-tooltip>
              </q-btn>
            </div>
          </q-item-section>
        </q-item>
      </q-list>

      <q-dialog v-model="listaOcorrencias" @hide="fecharOcorrencias()">
        <q-card style="width: 800px; max-width: 90vw">
          <q-card-section class="row items-center q-pb-none">
            <div class="text-h6">{{ ocorrenciasTitulo }}</div>
            <q-space />
            <q-btn flat round dense icon="close" v-close-popup />
          </q-card-section>

          <q-card-section v-if="carregandoOcorrencias" class="text-center q-pa-lg">
            <q-spinner color="primary" size="2em" />
          </q-card-section>

          <q-card-section v-else class="q-pt-sm">
            <div class="text-caption text-grey-7 q-mb-sm">
              {{ ocorrencias.length }}
              {{ ocorrencias.length === 1 ? "ocorrência" : "ocorrências" }}
            </div>

            <q-list separator dense class="ocorrencias-lista">
              <q-item v-for="ocorrencia in ocorrencias" :key="ocorrencia.id">
                <q-item-section>
                  <q-item-label>{{ ocorrencia.mensagem }}</q-item-label>
                  <q-item-label caption>
                    {{ $formatDateTime(ocorrencia.created) }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>
      </q-dialog>

      <q-dialog v-model="visualizador" @hide="fecharVisualizador()">
        <q-card style="width: 800px; max-width: 90vw">
          <q-card-section class="row items-center q-pb-none">
            <div class="text-h6">{{ conteudoTitulo }}</div>
            <q-space />
            <q-btn flat round dense icon="close" v-close-popup />
          </q-card-section>

          <q-card-section v-if="carregandoConteudo" class="text-center q-pa-lg">
            <q-spinner color="primary" size="2em" />
          </q-card-section>

          <!-- conteudo so e preenchido por produtor interno confiavel (nota de versao escrita
               por administrador DouraSoft). Mensagem de sistema externo vai em `mensagem`,
               que e exibido como texto escapado na listagem. -->
          <q-card-section v-else class="notificacao-conteudo" v-html="conteudoHtml"></q-card-section>
        </q-card>
      </q-dialog>

      <q-card-actions v-if="temMaisNotificacoes" align="center">
        <q-btn
          flat
          no-caps
          color="primary"
          label="Carregar mais"
          id="btn-carregar-mais-notificacoes"
          :loading="notificacoesLoadingMais"
          @click="carregarMais()"
        />
      </q-card-actions>
    </q-card>
  </q-page>
</template>

<script>
import { mapGetters } from "vuex";

const CORES_SEVERIDADE = {
  info: "blue",
  aviso: "orange",
  erro: "red",
};

export default {
  name: "notificacoes",

  data() {
    return {
      filtro: "todas",
      visualizador: false,
      carregandoConteudo: false,
      conteudoTitulo: "",
      conteudoHtml: "",
      listaOcorrencias: false,
      carregandoOcorrencias: false,
      ocorrenciasTitulo: "",
      ocorrencias: [],
    };
  },

  computed: {
    ...mapGetters([
      "notificacoes",
      "notificacoesLoading",
      "notificacoesLoadingMais",
      "notificacoesFiltroStatus",
      "temMaisNotificacoes",
      "pushSuportado",
      "pushConfigurado",
      "pushAtivo",
      "pushProcessando",
    ]),
  },

  created() {
    this.filtro = this.notificacoesFiltroStatus;
    this.$store.dispatch("fetchNotificacoes");
    this.$store.dispatch("verificarStatusPush");
  },

  methods: {
    corDaSeveridade(severidade) {
      return CORES_SEVERIDADE[severidade] || "grey-7";
    },

    // item agregado: created é a primeira ocorrência, atualizado_em é a última
    ultimaOcorrencia(notificacao) {
      return notificacao.atualizado_em || notificacao.created;
    },

    async alternarPush(ativar) {
      const resultado = ativar
        ? await this.$store.dispatch("inscreverPush")
        : await this.$store.dispatch("desinscreverPush");

      if (resultado.ok) {
        this.$q.notify({
          message: ativar
            ? "Avisos ativados neste dispositivo."
            : "Avisos desativados neste dispositivo.",
          color: "green",
          position: "top-right",
        });
        return;
      }

      this.$q.notify({
        message: resultado.motivo,
        color: "orange",
        position: "top-right",
      });
    },

    alterarFiltro(status) {
      this.$store.dispatch("alterarFiltroNotificacoes", status);
    },

    carregarMais() {
      this.$store.dispatch("fetchNotificacoes", { append: true });
    },

    // clicar sempre marca como lido. Com conteúdo, abre o visualizador; sem conteúdo,
    // leva ao que originou a notificação, quando houver url.
    async abrir(notificacao) {
      if (!notificacao.lido) {
        await this.$store.dispatch("marcarNotificacaoLida", notificacao.id);
      }

      // item agrupado abre o detalhe das ocorrências; item com conteúdo abre o visualizador
      if (notificacao.contador > 1) {
        this.abrirOcorrencias(notificacao);
        return;
      }

      if (notificacao.tem_conteudo) {
        this.abrirVisualizador(notificacao);
        return;
      }

      if (notificacao.url) {
        this.$router.push(notificacao.url);
      }
    },

    async abrirOcorrencias(notificacao) {
      this.ocorrenciasTitulo = notificacao.titulo;
      this.ocorrencias = [];
      this.carregandoOcorrencias = true;
      this.listaOcorrencias = true;

      const dados = await this.$store.dispatch(
        "fetchNotificacaoOcorrencias",
        notificacao.id
      );

      this.carregandoOcorrencias = false;

      if (!dados) {
        this.listaOcorrencias = false;
        this.$q.notify({
          message: "Não foi possível carregar as ocorrências da notificação.",
          color: "red",
          position: "top-right",
        });
        return;
      }

      this.ocorrencias = dados.ocorrencias || [];
    },

    fecharOcorrencias() {
      this.ocorrenciasTitulo = "";
      this.ocorrencias = [];
    },

    async abrirVisualizador(notificacao) {
      this.conteudoTitulo = notificacao.titulo;
      this.conteudoHtml = "";
      this.carregandoConteudo = true;
      this.visualizador = true;

      const dados = await this.$store.dispatch(
        "fetchNotificacaoConteudo",
        notificacao.id
      );

      this.carregandoConteudo = false;

      if (!dados) {
        this.visualizador = false;
        this.$q.notify({
          message: "Não foi possível carregar o conteúdo da notificação.",
          color: "red",
          position: "top-right",
        });
        return;
      }

      this.conteudoHtml = dados.conteudo || "";
    },

    fecharVisualizador() {
      this.conteudoTitulo = "";
      this.conteudoHtml = "";
    },

    async alternarLeitura(notificacao) {
      const action = notificacao.lido
        ? "marcarNotificacaoNaoLida"
        : "marcarNotificacaoLida";

      const ok = await this.$store.dispatch(action, notificacao.id);

      if (!ok) {
        this.$q.notify({
          message: "Não foi possível alterar o status da notificação.",
          color: "red",
          position: "top-right",
        });
      }
    },

    async excluir(notificacao) {
      const ok = await this.$store.dispatch("excluirNotificacao", notificacao.id);

      this.$q.notify({
        message: ok ? "Notificação excluída." : "Não foi possível excluir a notificação.",
        color: ok ? "green" : "red",
        position: "top-right",
      });
    },

    async marcarTodas() {
      const ok = await this.$store.dispatch("marcarTodasNotificacoesLidas");

      this.$q.notify({
        message: ok
          ? "Notificações marcadas como lidas."
          : "Não foi possível marcar as notificações como lidas.",
        color: ok ? "green" : "red",
        position: "top-right",
      });
    },
  },
};
</script>

<style lang="stylus" scoped>
// o conteúdo vem como HTML livre (nota de versão), então precisa de teto de altura e
// rolagem própria para não estourar o diálogo
.notificacao-conteudo {
  max-height: 65vh;
  overflow-y: auto;
}

// um lote de fila pode ter dezenas de ocorrências
.ocorrencias-lista {
  max-height: 55vh;
  overflow-y: auto;
}
</style>
